import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FiDatabase, FiFileText, FiFolder, FiLayers } from 'react-icons/fi';
import { departmentAPI, fileAPI, projectAPI } from '../utils/api';
import './DashboardPage.css';

const normalizeFilesData = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.files)) return data.files;
  return [];
};

const AnalyticsReportsPage = () => {
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        const [deptsResponse, projectsResponse, filesResponse] = await Promise.all([
          departmentAPI.getAllDepartments(),
          projectAPI.getAllProjects(),
          fileAPI.getAllFiles()
        ]);

        setDepartments(Array.isArray(deptsResponse?.data) ? deptsResponse.data : []);
        setProjects(Array.isArray(projectsResponse?.data) ? projectsResponse.data : []);
        setFiles(normalizeFilesData(filesResponse?.data));
      } catch (err) {
        setError('Failed to load analytics reports.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fileToProjectMap = useMemo(() => {
    const map = new Map();
    projects.forEach((project) => {
      map.set(Number(project.id), project);
    });
    return map;
  }, [projects]);

  const departmentRows = useMemo(() => {
    return departments.map((department) => {
      const departmentProjects = projects.filter(
        (project) => Number(project.departmentId) === Number(department.id)
      );
      const projectIds = new Set(departmentProjects.map((project) => Number(project.id)));
      const departmentFiles = files.filter((file) => projectIds.has(Number(file.projectId)));

      return {
        id: department.id,
        name: department.name || 'N/A',
        projectCount: departmentProjects.length,
        fileCount: departmentFiles.length,
        activeProjects: departmentProjects.filter(
          (project) => String(project.status || '').toUpperCase() === 'ACTIVE'
        ).length
      };
    });
  }, [departments, projects, files]);

  const totalProjects = projects.length;
  const totalFiles = files.length;
  const recentUploads = useMemo(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);

    return files.filter((file) => {
      const uploadedAt = new Date(file?.createdAt || file?.updatedAt || 0);
      return uploadedAt.getTime() >= sevenDaysAgo.getTime();
    }).length;
  }, [files]);

  const latestFiles = useMemo(() => {
    return [...files]
      .sort((a, b) => {
        const aDate = new Date(a?.createdAt || a?.updatedAt || 0).getTime();
        const bDate = new Date(b?.createdAt || b?.updatedAt || 0).getTime();
        return bDate - aDate;
      })
      .slice(0, 8)
      .map((file) => {
        const project = fileToProjectMap.get(Number(file.projectId));
        return {
          id: file.id,
          fileName: file.fileName || '-',
          fileType: file.fileType || '-',
          projectName: project?.name || '-',
          uploadedAt: formatDateTime(file.createdAt || file.updatedAt)
        };
      });
  }, [files, fileToProjectMap]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0">Loading analytics reports...</p>
      </div>
    );
  }

  return (
    <Container fluid className="dashboard-page py-4">
      <div className="mb-4">
        <h1 className="page-title">Analytics Reports</h1>
        <p className="page-subtitle mb-0">Department-wise project and file activity.</p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="g-3 mb-4">
        <Col md={3}>
          <MetricCard title="Departments" value={departments.length} icon={<FiLayers size={22} />} variant="primary" />
        </Col>
        <Col md={3}>
          <MetricCard title="Projects" value={totalProjects} icon={<FiFolder size={22} />} variant="info" />
        </Col>
        <Col md={3}>
          <MetricCard title="Files" value={totalFiles} icon={<FiFileText size={22} />} variant="success" />
        </Col>
        <Col md={3}>
          <MetricCard title="Uploads (7d)" value={recentUploads} icon={<FiDatabase size={22} />} variant="warning" />
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100 card-accent-top">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">Department Report</h5>
            </Card.Header>
            <Card.Body>
              {departmentRows.length === 0 ? (
                <div className="empty-state">
                  <div className="icon-chip icon-chip-lg icon-chip-secondary"><FiLayers /></div>
                  <div className="empty-state-title">No report rows available</div>
                  <p className="empty-state-subtitle mb-0">Department data will appear here once projects exist.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Department</th>
                        <th>Projects</th>
                        <th>Active Projects</th>
                        <th>Files</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departmentRows.map((row, index) => (
                        <tr key={row.id || index}>
                          <td>{index + 1}</td>
                          <td>{row.name}</td>
                          <td>{row.projectCount}</td>
                          <td>{row.activeProjects}</td>
                          <td>{row.fileCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100 card-accent-top">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">Recent Uploads</h5>
            </Card.Header>
            <Card.Body>
              {latestFiles.length === 0 ? (
                <div className="empty-state">
                  <div className="icon-chip icon-chip-lg icon-chip-secondary"><FiFileText /></div>
                  <div className="empty-state-title">No uploaded files found</div>
                  <p className="empty-state-subtitle mb-0">Recently uploaded files will show up here.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>File</th>
                        <th>Project</th>
                        <th>Type</th>
                        <th>Uploaded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latestFiles.map((file) => (
                        <tr key={file.id}>
                          <td>{file.fileName}</td>
                          <td>{file.projectName}</td>
                          <td>{file.fileType}</td>
                          <td>{file.uploadedAt}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const MetricCard = ({ title, value, icon, variant = 'primary' }) => {
  return (
    <Card className={`stat-card stat-${variant} shadow-sm h-100`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <p className="stat-title">{title}</p>
            <h2 className="stat-value">{value}</h2>
          </div>
          <div className="stat-icon">{icon}</div>
        </div>
      </Card.Body>
    </Card>
  );
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default AnalyticsReportsPage;
